import { useEffect, useRef, useState } from 'react'
import api from 'helpers/api'
import { useAuth } from 'hooks/auth'
import { getCurrentUser, getSessionExpiryInfo } from 'helpers/auth'

const getErrorStatus = (err: any): number | undefined => {
  return err?.rawCode ?? err?.response?.status
}

const CHECK_INTERVAL_MS = 10_000
const REFRESH_THRESHOLD_MIN = 10
const WARNING_THRESHOLD_MIN = 2
const FORCE_LOGOUT_SEC = 30

export function useSlidingSession(): {
  showWarningModal: boolean
  secondsUntilExpiry: number
  handleRemainSignedIn: () => Promise<void>
} {
  const { user, updateUser, logout } = useAuth()
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [secondsUntilExpiry, setSecondsUntilExpiry] = useState(0)
  const isRefreshingRef = useRef(false)
  const hasLoggedOutRef = useRef(false)

  // Reset the "already logged out" flag when a fresh token appears (user logs in again)
  useEffect(() => {
    if (user?.authTokenExpiresAt) {
      hasLoggedOutRef.current = false
    }
  }, [user?.authTokenExpiresAt])

  useEffect(() => {
    if (!user?.authTokenExpiresAt) {
      setShowWarningModal(false)
      setSecondsUntilExpiry(0)
      return
    }

    const checkAndRefresh = async () => {
      // If we've already triggered logout from this hook, do nothing
      if (hasLoggedOutRef.current) return

      const expiryInfo = getSessionExpiryInfo(user)
      if (!expiryInfo) {
        setShowWarningModal(false)
        return
      }

      // Invalid expiry date → logout once
      if (expiryInfo.isInvalid) {
        setShowWarningModal(false)
        hasLoggedOutRef.current = true
        logout()
        return
      }

      const secToExp = expiryInfo.secToExp
      const minToExp = secToExp / 60

      // Already expired → logout once
      if (expiryInfo.isExpired) {
        setShowWarningModal(false)
        hasLoggedOutRef.current = true
        logout()
        return
      }

      // Hard cutoff: in final countdown with modal already shown
      if (secToExp <= FORCE_LOGOUT_SEC && showWarningModal) {
        setShowWarningModal(false)
        hasLoggedOutRef.current = true
        logout()
        return
      }

      // Show warning modal (no auto-refresh inside this window)
      if (minToExp <= WARNING_THRESHOLD_MIN) {
        setShowWarningModal(true)
        setSecondsUntilExpiry(Math.max(0, Math.floor(secToExp)))
        return
      }

      // Auto-refresh if getting close AND tab is active
      if (minToExp <= REFRESH_THRESHOLD_MIN && !document.hidden && !isRefreshingRef.current) {
        isRefreshingRef.current = true
        let refreshData: any = null
        let refreshError: any = null
        try {
          const result = await api.refreshSession()
          refreshData = result.data
        } catch (err) {
          refreshError = err
        }

        if (refreshData) {
          // Guard: if logout happened while the refresh was in-flight, discard the result.
          // Check both the internal flag and localStorage (logout from elsewhere clears localStorage but not the ref).
          const stillLoggedIn = !hasLoggedOutRef.current && getCurrentUser()
          if (stillLoggedIn) {
            updateUser(refreshData)
            setShowWarningModal(false)
          }
        } else if (refreshError) {
          const status = getErrorStatus(refreshError)
          if (status === 401 || status === 403) {
            if (!hasLoggedOutRef.current) {
              hasLoggedOutRef.current = true
              logout()
            }
          } else {
            console.error('Failed to auto-refresh session:', refreshError)
          }
        }
        isRefreshingRef.current = false
      }
    }

    const intervalId = window.setInterval(() => {
      void checkAndRefresh()
    }, CHECK_INTERVAL_MS)

    void checkAndRefresh()

    return () => {
      window.clearInterval(intervalId)
    }
  }, [user?.authTokenExpiresAt, updateUser, logout, showWarningModal])

  const handleRemainSignedIn = async () => {
    if (isRefreshingRef.current || hasLoggedOutRef.current) return

    isRefreshingRef.current = true
    let refreshData: any = null
    let refreshError: any = null
    try {
      const result = await api.refreshSession()
      refreshData = result.data
    } catch (err) {
      refreshError = err
    }

    if (refreshData) {
      const stillLoggedIn = !hasLoggedOutRef.current && getCurrentUser()
      if (stillLoggedIn) {
        updateUser(refreshData)
        setShowWarningModal(false)
      }
    } else if (refreshError) {
      const status = getErrorStatus(refreshError)
      if (status === 401 || status === 403) {
        if (!hasLoggedOutRef.current) {
          hasLoggedOutRef.current = true
          logout()
        }
      } else {
        console.error('Failed to refresh session via modal:', refreshError)
      }
    }
    isRefreshingRef.current = false
  }

  return {
    showWarningModal,
    secondsUntilExpiry,
    handleRemainSignedIn
  }
}
